import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Buildings,
  CheckCircle,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import * as shareSettingsApi
  from "@/api/shareSettings";
import {
  DEFAULT_SHARE_SETTINGS,
  resolveShareSettings,
} from "@/lib/shareMessageTemplate";
import { formatApiError } from "@/lib/api";


const VARIABLES = [
  "{{firstName}}",
  "{{lastName}}",
  "{{company}}",
  "{{tenantName}}",
  "{{senderName}}",
  "{{documents}}",
  "{{shareUrl}}",
];


function errorMessage(
  error,
  fallback
) {
  return (
    formatApiError(
      error?.response?.data?.detail
    )
    || error?.message
    || fallback
  );
}


function toPayload(settings) {
  return {
    email_subject:
      settings.emailSubject,
    outbound_message:
      settings.outboundMessage,
    welcome_message:
      settings.welcomeMessage,
  };
}


function Editor({
  value,
  onChange,
  onSave,
  onRestore,
  saving,
  saveLabel,
  restoreLabel,
  testId,
}) {
  return (
    <div
      className="space-y-4"
      data-testid={testId}
    >
      <label className="block">
        <span className="text-xs font-semibold text-[var(--do-ink)]">
          Objet de l’e-mail
        </span>

        <input
          value={value.emailSubject}
          onChange={(event) =>
            onChange({
              ...value,
              emailSubject:
                event.target.value,
            })
          }
          className="mt-2 h-10 w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-xs outline-none focus:border-[var(--do-accent)]"
          data-testid={`${testId}-subject`}
        />
      </label>

      <label className="block">
        <span className="text-xs font-semibold text-[var(--do-ink)]">
          Message envoyé avec le lien
        </span>

        <textarea
          value={value.outboundMessage}
          onChange={(event) =>
            onChange({
              ...value,
              outboundMessage:
                event.target.value,
            })
          }
          rows={10}
          className="mt-2 w-full resize-y rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-xs leading-5 outline-none focus:border-[var(--do-accent)]"
          data-testid={`${testId}-outbound`}
        />
      </label>

      <label className="block">
        <span className="text-xs font-semibold text-[var(--do-ink)]">
          Message d’accueil dans l’espace sécurisé
        </span>

        <textarea
          value={value.welcomeMessage}
          onChange={(event) =>
            onChange({
              ...value,
              welcomeMessage:
                event.target.value,
            })
          }
          rows={7}
          className="mt-2 w-full resize-y rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-xs leading-5 outline-none focus:border-[var(--do-accent)]"
          data-testid={`${testId}-welcome`}
        />
      </label>

      <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-3">
        <div className="text-xs font-semibold text-[var(--do-ink)]">
          Variables disponibles
        </div>

        <div className="mt-2 flex flex-wrap gap-2">
          {VARIABLES.map(
            (variable) => (
              <code
                key={variable}
                className="rounded-md border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2 py-1 text-[10px] text-[var(--do-muted)]"
              >
                {variable}
              </code>
            )
          )}
        </div>

        <p className="mt-2 text-[10px] text-[var(--do-muted)]">
          Le message envoyé doit conserver la variable {"{{shareUrl}}"} afin d’inclure le lien sécurisé.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-[var(--do-blue)] px-4 text-xs font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
        >
          <CheckCircle
            size={16}
            weight="fill"
          />

          {saveLabel}
        </button>

        {onRestore && (
          <button
            type="button"
            onClick={onRestore}
            disabled={saving}
            className="h-9 rounded-lg border border-[var(--do-border)] px-4 text-xs font-heading font-bold text-[var(--do-ink)] disabled:opacity-50"
          >
            {restoreLabel}
          </button>
        )}
      </div>
    </div>
  );
}


export default function ShareMessageSettings({
  canManageTenant = false,
}) {
  const [
    personal,
    setPersonal,
  ] = useState(
    DEFAULT_SHARE_SETTINGS
  );

  const [
    tenantStored,
    setTenantStored,
  ] = useState({});

  const [
    tenantEditor,
    setTenantEditor,
  ] = useState(
    DEFAULT_SHARE_SETTINGS
  );

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const tenantEffective =
    useMemo(
      () =>
        resolveShareSettings(
          {},
          tenantStored
        ),
      [tenantStored]
    );

  useEffect(() => {
    let mounted = true;

    Promise.all([
      shareSettingsApi.getMine(),
      shareSettingsApi.getTenant(),
    ])
      .then(
        ([
          mineResult,
          tenantResult,
        ]) => {
          if (!mounted) return;

          const tenant =
            tenantResult?.settings
            || {};

          setTenantStored(
            tenant
          );

          setTenantEditor(
            resolveShareSettings(
              {},
              tenant
            )
          );

          setPersonal(
            resolveShareSettings(
              mineResult?.settings
              || {},
              tenant
            )
          );
        }
      )
      .catch((error) => {
        toast.error(
          errorMessage(
            error,
            "Impossible de charger les messages de partage."
          )
        );
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const savePersonal =
    async () => {
      setSaving(true);

      try {
        const result =
          await shareSettingsApi
            .updateMine(
              toPayload(
                personal
              )
            );

        setPersonal(
          resolveShareSettings(
            result?.settings || {},
            tenantStored
          )
        );

        toast.success(
          "Votre message par défaut a été enregistré."
        );
      } catch (error) {
        toast.error(
          errorMessage(
            error,
            "Impossible d’enregistrer votre message."
          )
        );
      } finally {
        setSaving(false);
      }
    };

  const saveTenant =
    async () => {
      setSaving(true);

      try {
        const result =
          await shareSettingsApi
            .updateTenant(
              toPayload(
                tenantEditor
              )
            );

        const settings =
          result?.settings
          || {};

        setTenantStored(
          settings
        );

        setTenantEditor(
          resolveShareSettings(
            {},
            settings
          )
        );

        toast.success(
          "Le modèle de la société a été enregistré."
        );
      } catch (error) {
        toast.error(
          errorMessage(
            error,
            "Impossible d’enregistrer le modèle société."
          )
        );
      } finally {
        setSaving(false);
      }
    };

  if (loading) {
    return (
      <div className="lirah-card p-5 text-sm text-[var(--do-muted)]">
        Chargement des paramètres de partage…
      </div>
    );
  }

  return (
    <div
      className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(24rem,100%),1fr))] gap-4"
      data-testid="settings-secure-share-panel"
    >
      <section className="lirah-card settings-section-card p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
            <EnvelopeSimple
              size={20}
              weight="duotone"
            />
          </div>

          <div>
            <h2 className="font-heading text-base font-black text-[var(--do-ink)]">
              Mon message par défaut
            </h2>

            <p className="mt-1 text-xs text-[var(--do-muted)]">
              Ce modèle est utilisé uniquement pour votre compte. Vous pourrez toujours le modifier juste avant l’envoi.
            </p>
          </div>
        </div>

        <div className="mt-5">
          <Editor
            value={personal}
            onChange={setPersonal}
            onSave={savePersonal}
            onRestore={() =>
              setPersonal(
                tenantEffective
              )
            }
            saving={saving}
            saveLabel="Enregistrer mon modèle"
            restoreLabel="Restaurer le modèle société"
            testId="personal-share-message"
          />
        </div>
      </section>

      {canManageTenant && (
        <section className="lirah-card settings-section-card p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
              <Buildings
                size={20}
                weight="duotone"
              />
            </div>

            <div>
              <h2 className="font-heading text-base font-black text-[var(--do-ink)]">
                Modèle de la société
              </h2>

              <p className="mt-1 text-xs text-[var(--do-muted)]">
                Modèle utilisé lorsqu’un utilisateur n’a pas encore défini son propre message.
              </p>
            </div>
          </div>

          <div className="mt-5">
            <Editor
              value={tenantEditor}
              onChange={setTenantEditor}
              onSave={saveTenant}
              saving={saving}
              saveLabel="Enregistrer le modèle société"
              testId="tenant-share-message"
            />
          </div>
        </section>
      )}
    </div>
  );
}
